import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {ROLL_TYPES, BEAST_OUTCOMES, canSeeRoll, getRollVerdict, findBeastMoment, findSharedRoll} from '../rolls';
import {useHungerEffectsEnabled} from '../hungerEffects';
import VtmBeastMoment from './VtmBeastMoment';
import {vtmT} from '../i18n';
import {VtmDie, outcomeText, outcomeTone, rouseDetail, PowerRouseLines} from './VtmDice';

const KIND_LABELS = {bloodSurge: 'bloodSurge', impaired: 'impairedPenalty', modifier: 'modifier'};

/** « Charisme 4 + Persuasion 3 + Négociation 1 − Diminué 2 » */
const formatBreakdown = (breakdown) => breakdown
  .map((part, index) => {
    const label = KIND_LABELS[part.kind] ? vtmT(KIND_LABELS[part.kind]) : part.label;
    const sign = part.value < 0 ? '− ' : (index > 0 ? '+ ' : '');
    return `${sign}${label} ${Math.abs(part.value)}`;
  })
  .join(' ');

const BEAST_MS = 2600;
const CARD_MS = 5000;

/**
 * Jet qui vient d'arriver dans le chat (pas l'historique chargé à l'ouverture), chez ceux qui peuvent le voir.
 * `snapshot` résume chaque jet d'un rendu à l'autre, `find(précédents, jets visibles)` choisit le jet à montrer.
 * @returns {[Object|null, Function]} {key, record, id} et de quoi le masquer
 */
const useRollMoment = (rolls, userUid, dmUid, {snapshot, find, duration}) => {
  const seen = useRef(null);
  const [moment, setMoment] = useState(null);

  useEffect(() => {
    // L'historique chargé à l'ouverture (plusieurs jets d'un coup) ne déclenche rien
    const historyLoad = !seen.current || (Object.keys(seen.current).length === 0 && rolls.length > 1);
    const found = !historyLoad ? find(seen.current, rolls.filter(record => canSeeRoll(record, userUid, dmUid))) : null;
    seen.current = Object.fromEntries(rolls.map(record => [record.key, snapshot(record)]));
    if (found) setMoment({key: found.key, record: found, id: Date.now()});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolls, userUid, dmUid]);

  useEffect(() => {
    if (!moment) return undefined;
    const timer = setTimeout(() => setMoment(null), duration);
    return () => clearTimeout(timer);
  }, [moment, duration]);

  return [moment, () => setMoment(null)];
};

const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});

const RollBody = ({record}) => {
  switch (record.type) {
    case ROLL_TYPES.ROUSE:
      return (
        <p className="vtmChatLine">
          <strong>{vtmT('rouseCheck')}</strong> {rouseDetail(record.rouse)}
        </p>
      );
    case ROLL_TYPES.POWER:
      return (
        <>
          <p className="vtmChatLine"><strong>{record.label}</strong></p>
          <PowerRouseLines checks={record.powerRouses} className="vtmChatMuted" />
        </>
      );
    case ROLL_TYPES.MEND:
      return (
        <>
          <p className="vtmChatLine">
            <strong>{vtmT('mendCheck')}</strong> {vtmT('mendHealed', {value: record.mend.healed})}
          </p>
          <p className="vtmChatMuted">{vtmT('rouseCheck')} : {rouseDetail(record.rouse)}</p>
        </>
      );
    case ROLL_TYPES.FEED:
      return (
        <p className="vtmChatLine">
          <strong>{vtmT('feedCheck')}</strong> {vtmT('feedHunger', {from: record.feed.from, to: record.feed.to})}
          {record.feed.note && <span className="vtmChatBreakdown"> · {record.feed.note}</span>}
        </p>
      );
    case ROLL_TYPES.XP:
      return (
        <p className="vtmChatLine">
          <strong>{vtmT('xpPurchase')}</strong> {record.xp.summary} <span className="vtmChatTag">−{record.xp.cost} XP</span>
        </p>
      );
    case ROLL_TYPES.REMORSE:
      return (
        <>
          <p className="vtmChatLine"><strong>{vtmT('remorseCheck')}</strong> {record.remorse.dice.join(' · ')}</p>
          <p className={`vtmChatOutcome ${record.remorse.success ? 'bone' : 'blood'}`}>
            {record.remorse.success ? vtmT('remorseSuccess') : vtmT('remorseFail', {value: record.remorse.newHumanity})}
          </p>
        </>
      );
    default:
      return (
        <>
          <p className="vtmChatLine">
            <strong>{record.type === ROLL_TYPES.FRENZY ? vtmT('frenzyCheck') : record.label}</strong>
            {record.breakdown.length > 0 && <span className="vtmChatBreakdown"> {formatBreakdown(record.breakdown)}</span>}
          </p>
          {(record.regular.length > 0 || record.hunger.length > 0) && (
            <div className="vtmChatDice">
              {record.regular.map((value, index) => (
                <VtmDie key={`r${index}`} value={value} small rerolled={record.rerolledIndexes.includes(index)} />
              ))}
              {record.hunger.map((value, index) => <VtmDie key={`h${index}`} value={value} hunger small />)}
            </div>
          )}
          <p className={`vtmChatOutcome ${outcomeTone(record.outcome)}`}>
            {outcomeText(record)}
            {record.outcome && ` · ${record.successes} ${vtmT('successes')}`}
            {record.willpowerRerolled && <span className="vtmChatTag"> {vtmT('rerolledTag')}</span>}
          </p>
          <PowerRouseLines checks={record.powerRouses} className="vtmChatMuted" />
          {record.rouse && <p className="vtmChatMuted">{vtmT('bloodSurge')} : {rouseDetail(record.rouse)}</p>}
        </>
      );
  }
};

/** Jet d'un autre membre de la table, en grand en haut de l'écran ; un clic le masque. */
const VtmRollCard = ({record, onDismiss}) => {
  const verdict = getRollVerdict(record);
  const beast = BEAST_OUTCOMES.includes(record.outcome);
  return (
    <div className={`vtmRollCard${verdict ? ` ${verdict}` : ''}${beast ? ' beast' : ''}`} role="status">
      <button type="button" className="vtmRollCardDismiss" onClick={onDismiss} aria-label={vtmT('dismiss')} title={vtmT('dismiss')}>×</button>
      <div className="vtmRollCardHead">
        {record.picture && <span className="vtmRollCardPortrait" style={{backgroundImage: `url(${record.picture})`}} aria-hidden="true" />}
        <span className="vtmRollCardName">{record.characterName}</span>
      </div>
      <div className="vtmRollCardBody">
        <RollBody record={record} />
      </div>
    </div>
  );
};

/**
 * Historique des jets de la campagne, le plus récent en bas.
 * @param {String} [props.reopenKey] jet qu'un clic rouvre (dernier jet du personnage, encore relançable à la Volonté)
 */
const VtmRollChat = ({rolls, userUid, dmUid, reopenKey, onReopen}) => {
  const listRef = useRef(null);
  const followRef = useRef(true);
  const visible = rolls.slice(-100);
  const effects = useHungerEffectsEnabled();
  // Critique sanglant ou échec bestial : effet visible par toute la table
  const [beast] = useRollMoment(rolls, userUid, dmUid, {
    snapshot: record => record.outcome,
    find: findBeastMoment,
    duration: BEAST_MS,
  });
  // Jet d'un autre membre de la table : montré en grand quelques secondes
  const [shared, dismissShared] = useRollMoment(rolls, userUid, dmUid, {
    snapshot: record => Boolean(record.willpowerRerolled),
    find: (previous, visibleRolls) => findSharedRoll(previous, visibleRolls, userUid),
    duration: CARD_MS,
  });

  // On fait défiler la liste elle-même (scrollIntoView ferait aussi défiler la page),
  // et seulement si l'utilisateur est déjà en bas : il peut remonter lire l'historique.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list && followRef.current) list.scrollTop = list.scrollHeight;
  }, [rolls.length]);

  const onScroll = () => {
    const list = listRef.current;
    followRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
  };

  return (
    <section className="vtmChat" aria-label={vtmT('chat')}>
      {effects && beast && <VtmBeastMoment key={beast.id} outcome={beast.record.outcome} />}
      {shared && <VtmRollCard key={shared.id} record={shared.record} onDismiss={dismissShared} />}
      <h2 className="vtmChatTitle">{vtmT('chat')}</h2>
      <ol ref={listRef} className="vtmChatList" aria-live="polite" onScroll={onScroll}>
        {visible.length === 0 && <li className="vtmChatEmpty">{vtmT('noRolls')}</li>}
        {visible.map(record => {
          const allowed = canSeeRoll(record, userUid, dmUid);
          // Jamais de verdict sur un jet caché qu'on n'a pas le droit de voir
          const verdict = allowed ? getRollVerdict(record) : null;
          return (
            <li key={record.key} className={`vtmChatEntry${record.hidden ? ' hidden' : ''}${effects && beast?.key === record.key ? ` beast ${beast.record.outcome}` : ''}`}>
              <div className="vtmChatHeader">
                <span className="vtmChatName">
                  {verdict && (
                    <span
                      className={`vtmChatVerdict ${verdict}`}
                      role="img"
                      aria-label={vtmT(verdict === 'success' ? 'outcome_win' : 'outcome_failure')}
                      title={vtmT(verdict === 'success' ? 'outcome_win' : 'outcome_failure')}
                    />
                  )}
                  {record.characterName}
                </span>
                <span className="vtmChatTime">
                  {record.hidden && `${vtmT('hiddenRollOther')} · `}
                  {formatTime(record.createdAt)}
                </span>
              </div>
              {allowed ? <RollBody record={record} /> : <p className="vtmChatMuted">{vtmT('hiddenRollOther')}</p>}
              {onReopen && record.key === reopenKey && (
                <button type="button" className="vtmChatReopen" onClick={() => onReopen(record)}>{vtmT('reopenRoll')}</button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default VtmRollChat;
